/**
 * Размерные шкалы каталога.
 *
 * Фасет размеров собирался «как лежит»: значения из карточек шли одним списком,
 * отсортированным по числу товаров. Отсюда две беды, которые заказчик назвал на
 * демонстрации 15.09.2026: в обуви появлялись S / M / L, а порядок значений был
 * случайным («42, 36, 40»). Плюс демо-каталог держал два размера «M» — латиницей
 * и кириллицей, — и они не схлопывались.
 *
 * Здесь один источник правды для всех поверхностей: как размер выглядит после
 * нормализации, к какой шкале относится и в каком порядке идёт внутри неё.
 * Смешивать шкалы в одном ряду нельзя — но и прятать «общие» значения
 * («Универсальный», «One size») тоже нельзя: они законно встречаются везде.
 */

/** Шкала размера. Порядок перечисления = порядок групп в фильтре. */
export const SIZE_SCALES = [
  "letter",
  "numeric",
  "clothing",
  "shoes",
  "kids",
  "volume",
  "universal",
  "other",
] as const
export type SizeScale = (typeof SIZE_SCALES)[number]

/** Подписи групп для фильтра (русский интерфейс — язык площадки по умолчанию). */
export const SIZE_SCALE_LABELS: Record<SizeScale, string> = {
  letter: "Буквенные размеры",
  // Числовая сетка без выбранной категории: обувь и одежда пересекаются по
  // числам (42 — и то, и другое), и называть общий фасет обувным — враньё.
  numeric: "Числовые размеры",
  clothing: "Размеры одежды",
  shoes: "Размеры обуви",
  kids: "Детские размеры (рост)",
  volume: "Объём и длина",
  universal: "Универсальные",
  other: "Другие",
}

/** Буквенные размеры в естественном порядке, а не по алфавиту. */
const LETTER_ORDER = ["XXXS", "XXS", "XS", "S", "M", "L", "XL", "XXL", "XXXL", "4XL", "5XL"]

/**
 * Кириллические двойники латинских букв. В каталоге они неразличимы на глаз, но
 * для базы это разные строки: отсюда два «M» в одном фасете.
 */
const CYRILLIC_TO_LATIN: Record<string, string> = {
  А: "A",
  В: "B",
  С: "C",
  Е: "E",
  К: "K",
  М: "M",
  Н: "H",
  О: "O",
  Р: "P",
  Т: "T",
  Х: "X",
  У: "Y",
  Ь: "L",
}

const UNIVERSAL = ["УНИВЕРСАЛЬНЫЙ", "УНИВЕРСАЛЬНАЯ", "ONE SIZE", "ONESIZE", "OS", "БЕЗ РАЗМЕРА", "ЕДИНЫЙ"]

/**
 * Приведение размера к каноническому виду: без лишних пробелов, буквенные — в
 * верхнем регистре и латиницей, «one size» во всех написаниях — к одному
 * значению. Числовые размеры не трогаем, кроме обрезки пробелов и запятой в
 * дробях («41,5» → «41.5»), иначе одна и та же полуразмерность живёт дважды.
 */
export const normalizeSize = (raw: string): string => {
  const trimmed = raw.trim().replace(/\s+/g, " ")
  if (!trimmed) return ""
  const upper = trimmed.toUpperCase()
  // «Универсальный» проверяем ДО замены кириллицы: иначе слово распадается на
  // смесь алфавитов («УНИВЕРСАЛЬНЫЙ» → «YHИBEPCAЛЬHЫЙ») и не совпадает ни с чем.
  if (UNIVERSAL.includes(upper)) return "Универсальный"
  const latin = [...upper].map((ch) => CYRILLIC_TO_LATIN[ch] ?? ch).join("")
  if (UNIVERSAL.includes(latin)) return "Универсальный"
  if (LETTER_ORDER.includes(latin)) return latin
  // «41,5» и «41.5» — один размер.
  if (/^\d+[.,]\d+$/.test(latin)) return latin.replace(",", ".")
  return /^[A-Z0-9\s./-]+$/.test(latin) ? latin : trimmed
}

/**
 * Число, если размер числовой целиком («41», «41.5»). Строки вроде «1 шт» или
 * «2 пары» это НЕ размер, а единица измерения: раньше они проходили по первому
 * числу и оседали в обувной сетке рядом с 38-м размером.
 */
const numericPart = (value: string): number | null => {
  const match = value.match(/^(\d+(?:\.\d+)?)$/)
  if (!match) return null
  const n = Number(match[1])
  return Number.isFinite(n) ? n : null
}

/**
 * К какой шкале относится размер.
 *
 * Границы числовых диапазонов выбраны по российским сеткам: обувь 16–52,
 * одежда 38–70, детский рост 50–176 см. Пересечения неизбежны (42 — это и
 * обувь, и одежда), поэтому шкалу уточняет категория товара: функция принимает
 * подсказку и доверяет ей, когда та есть.
 */
export const sizeScaleOf = (value: string, hint?: SizeScale): SizeScale => {
  const size = normalizeSize(value)
  if (!size) return "other"
  if (size === "Универсальный") return "universal"
  if (LETTER_ORDER.includes(size)) return "letter"
  const n = numericPart(size)
  if (n == null) return "other"
  if (hint === "shoes" || hint === "clothing" || hint === "kids") return hint
  if (n >= 80) return "kids"
  // Без подсказки категории числовую сетку не делим: обувные и одёжные размеры
  // пересекаются, и «Размеры обуви» в общем фасете было бы неправдой. Как только
  // покупатель выбрал категорию, подсказка выше разводит их точно.
  return "numeric"
}

/** Порядок размеров внутри шкалы: буквы по сетке, числа по возрастанию. */
export const compareSizes = (a: string, b: string): number => {
  const left = normalizeSize(a)
  const right = normalizeSize(b)
  const li = LETTER_ORDER.indexOf(left)
  const ri = LETTER_ORDER.indexOf(right)
  if (li >= 0 && ri >= 0) return li - ri
  if (li >= 0) return -1
  if (ri >= 0) return 1
  const ln = numericPart(left)
  const rn = numericPart(right)
  if (ln != null && rn != null) return ln - rn
  if (ln != null) return -1
  if (rn != null) return 1
  return left.localeCompare(right, "ru")
}

export type SizeFacetValue = { value: string; count: number }
export type SizeFacetGroup = { scale: SizeScale; label: string; values: SizeFacetValue[] }

/**
 * Сборка фасета размеров: нормализация (склейка дублей), разбивка по шкалам и
 * естественный порядок внутри каждой.
 *
 * Обувь и буквенные размеры оказываются в разных группах, поэтому в обувной
 * категории ряд S / M / L просто не появляется. «Универсальный» остаётся
 * отдельной группой: это законное значение для любой категории.
 */
export const groupSizeFacet = (
  values: SizeFacetValue[],
  hint?: SizeScale,
): SizeFacetGroup[] => {
  const merged = new Map<string, number>()
  for (const item of values) {
    const size = normalizeSize(item.value)
    if (!size) continue
    merged.set(size, (merged.get(size) ?? 0) + item.count)
  }
  const byScale = new Map<SizeScale, SizeFacetValue[]>()
  for (const [size, count] of merged) {
    const scale = sizeScaleOf(size, hint)
    const list = byScale.get(scale) ?? []
    list.push({ value: size, count })
    byScale.set(scale, list)
  }
  return SIZE_SCALES.filter((scale) => byScale.has(scale)).map((scale) => ({
    scale,
    label: SIZE_SCALE_LABELS[scale],
    values: (byScale.get(scale) ?? []).sort((a, b) => compareSizes(a.value, b.value)),
  }))
}

/**
 * Подсказка шкалы по названию или пути категории. Категория «Обувь» и её
 * подкатегории всегда обувная сетка, даже если числа попадают в одежный
 * диапазон, — это и есть ответ на «выбрал обувь, а там S / M / L».
 */
export const sizeScaleForCategory = (categoryPath: string): SizeScale | undefined => {
  const path = categoryPath.toLowerCase()
  if (/обув|кроссов|ботин|сапог|туфл|кед|сандал|shoe|sneaker|boot/.test(path)) return "shoes"
  if (/детск|kids|малыш|baby|новорожд/.test(path)) return "kids"
  if (/одежд|платье|футбол|рубаш|брюк|джинс|куртк|пальто|свитер|clothing|apparel/.test(path)) {
    return "clothing"
  }
  return undefined
}
